import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiArrowRight, FiShield, FiStar} from 'react-icons/fi';

const BOOKING_URL = 'https://links.myvoicelocal.com/widget/bookings/10-minute-call-with-stu';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#faf8f5]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(217,119,6,0.08),transparent)]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-50 rounded-full blur-[120px] opacity-60 translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-stone-100 rounded-full blur-[100px] opacity-80 -translate-x-1/3 translate-y-1/3" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fill-rule=evenodd%3E%3Cg fill=%23d97706 fill-opacity=0.03%3E%3Cpath d=M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <motion.div
            initial={{opacity: 0, x: -40}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.9, ease: [0.16, 1, 0.3, 1]}}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-amber-500 fill-current" />
                ))}
              </div>
              <span className="text-stone-500 text-base font-medium">Physician-supervised weight loss</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-stone-900 tracking-tight leading-[1.05] font-serif mb-8">
              The 6-Week <br />
              <span className="shimmer-text">Body Reset</span>
              <br />
              <span className="font-light italic text-stone-400">Challenge</span>
            </h1>
            <p className="text-xl sm:text-2xl text-stone-600 leading-relaxed mb-10 font-light max-w-lg">
              Physician-supervised protocols, clinically appropriate prescription options, and relentless daily accountability. All in one seamless program.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <motion.a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{scale: 1.03, y: -2}}
                whileTap={{scale: 0.97}}
                className="group flex items-center justify-center gap-3 bg-stone-900 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl shadow-stone-900/25 hover:bg-amber-700 transition-all"
              >
                Claim Your Spot
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
              <motion.a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                className="flex items-center justify-center gap-2 border border-stone-200 bg-white/80 text-stone-700 px-8 py-5 rounded-2xl font-semibold text-lg hover:border-amber-400 hover:text-amber-700 transition-all"
              >
                <SafeIcon icon={FiShield} className="text-amber-600" />
                6-Week Results Guarantee
              </motion.a>
            </div>
            <div className="flex flex-wrap gap-6">
              {['Medical Supervision', 'Clinically Reviewed Rx', 'Direct Delivery'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-stone-500 text-base font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{opacity: 0, x: 40}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2}}
            className="relative hidden lg:flex items-center justify-center h-[580px]"
          >
            <div className="absolute w-[340px] h-[420px] bg-amber-50 rounded-[2.5rem] border border-amber-100 rotate-6 top-16 right-4 shadow-xl" />
            <div className="absolute w-[340px] h-[420px] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-stone-900/20 -rotate-2 float-card">
              <img
                src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070&auto=format&fit=crop"
                alt="Transformation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass rounded-2xl p-4">
                  <p className="text-stone-800 font-bold text-sm">Week 6 Progress</p>
                  <p className="text-amber-700 font-black text-2xl">-18 lbs</p>
                  <p className="text-stone-500 text-xs mt-0.5">Individual results vary</p>
                </div>
              </div>
            </div>
            <div className="absolute top-8 -left-4 glass rounded-2xl p-4 shadow-xl float-card-delay gold-border w-48">
              <p className="text-stone-500 text-xs font-bold uppercase tracking-wider mb-1">Avg. Weight Loss*</p>
              <p className="text-stone-900 font-black text-3xl font-serif">15%</p>
              <p className="text-stone-400 text-xs mt-0.5">*Individual results vary</p>
            </div>
            <div className="absolute bottom-12 -right-2 glass rounded-2xl p-4 shadow-xl float-card w-44">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Enrolling Now</span>
              </div>
              <p className="text-stone-900 font-bold text-sm">Limited spots available</p>
              <p className="text-stone-400 text-xs">Next cohort closes soon</p>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-stone-400 to-transparent" />
      </div>
    </section>
  );
};

export default Hero;